import { motion } from 'framer-motion';
import { Heart, Globe, Users, Compass } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Autenticità',
      description: 'Ogni viaggio che raccontiamo è reale, vissuto, sentito.'
    },
    {
      icon: Globe,
      title: 'Apertura culturale',
      description: 'Viaggiare significa immergersi nelle culture locali con rispetto e curiosità.'
    },
    {
      icon: Users,
      title: 'Famiglia',
      description: 'I nostri bambini sono parte integrante di ogni viaggio, non un limite.'
    },
    {
      icon: Compass,
      title: 'Slow travel',
      description: 'La lentezza ci permette di vivere i luoghi, assaporarli e lasciarci arricchire da ogni incontro.'
    }
  ];

  return (
    <div data-testid="about-page">
      <Helmet>
        <title>Chi Siamo | Blog Viaggi in Famiglia con Bambini — Chichi in Viaggio</title>
        <meta name="description" content="Siamo Nancy e Simone, una famiglia che viaggia lentamente con i bambini. Scopri la nostra storia, i nostri valori e perché crediamo nel viaggio lento." />
        <meta property="og:title" content="Chi Siamo — Chichi in Viaggio" />
        <meta property="og:description" content="Nancy e Simone: la storia di una famiglia italiana che viaggia con i bambini in modo lento e autentico." />
      </Helmet>
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_9cdb27cd-9e5d-42de-bb9a-935202cbb403/artifacts/xs7ujmh6_IMG_6412.jpeg"
                alt="Famiglia Chichi in Viaggio"
                className="rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] w-full"
                data-testid="about-nancy-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-6" data-testid="about-title">
                Chi siamo
              </h1>
              <div className="space-y-4 text-base md:text-lg leading-relaxed text-foreground/80" data-testid="about-content">
                <p>
                  Siamo Nancy e Simone, da sempre amiamo viaggiare e con l'arrivo dei nostri due bimbi abbiamo scoperto che esplorare il mondo con i loro occhi rende tutto ancora più magico.
                </p>
                <p>
                  <strong>Nancy:</strong> Viaggiare per me è ossigeno puro. Dopo tante mete, nel 2017 un Paese ha cambiato la mia visione di viaggio: l'India. Ogni volta mi emoziono guardando la vita scorrere dall'altra parte del mondo, mi riempio il cuore di sguardi profondi e sorrisi reali. Quando torno mi sento "in prestito" e non vedo l'ora di ripartire.
                </p>
                <p>
                  <strong>Simone:</strong> Inguaribile sportivo e sempre positivo. Instancabile viaggiatore, di ogni luogo amo scoprire gli angoli più insoliti lontani dalle rotte turistiche.
                </p>
                <p>
                  <strong>I nostri bambini:</strong> Due piccoli esploratori che amano le avventure, la natura, il fango e le risate. Sono vivaci, complici, curiosi e con gli occhi pieni di meraviglia.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-muted" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-12 text-center"
            data-testid="values-title"
          >
            I nostri valori
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="text-primary" size={24} />
                </div>
                <h3 className="font-serif text-2xl mb-3">{value.title}</h3>
                <p className="text-foreground/70 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="vision-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-4" data-testid="vision-title">
              La nostra missione
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto rounded-full" />
          </motion.div>

          <div className="space-y-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="flex gap-6 items-start"
            >
              <div className="hidden md:block w-[3px] bg-primary/40 self-stretch rounded-full flex-shrink-0" />
              <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground/90">
                La nostra missione è <strong>accompagnare i nostri figli</strong> in un cammino di crescita fatto di apertura mentale, curiosità viva e desiderio autentico di scoprire il mondo, dentro e fuori di noi.
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-lg leading-relaxed text-foreground/70 md:pl-9"
            >
              Crediamo in un'<strong>educazione che non si limiti ai confini di un'aula</strong>, ma che respiri nei vicoli di una città sconosciuta, nei silenzi della natura, nei racconti ascoltati in lingue diverse.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="border-l-4 border-primary/60 pl-8 py-4"
            >
              <p className="text-lg leading-relaxed text-foreground/80">
                Vogliamo che i nostri figli imparino a <strong>connettersi con le persone</strong> prima ancora che con i luoghi, a riconoscere negli sguardi sinceri un linguaggio universale, a sentirsi parte di un mondo ricco di differenze che non dividono, ma arricchiscono.
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="font-serif italic text-2xl md:text-3xl text-center text-primary pt-4"
            >
              "La vera ricchezza non è quanta strada fai, ma quanto profondamente la vivi."
            </motion.p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;